import { useOrganization } from "@clerk/react";

/** Non-empty array — at least one element. */
type NonEmptyArray<A> = readonly [A, ...A[]];

type Permission = string;

export type PermissionProps = Omit<ProtectedProps, "children">;

export type ProtectedProps =
  & {
    readonly children: React.ReactNode;
    readonly requiresSuperAdmin?: boolean;
  }
  & (
    | {
      readonly requiresAll: NonEmptyArray<Permission>;
      readonly requiresAny?: never;
      readonly requires?: never;
    }
    | {
      readonly requiresAny: NonEmptyArray<Permission>;
      readonly requiresAll?: never;
      readonly requires?: never;
    }
    | {
      readonly requires: Permission;
      readonly requiresAll?: never;
      readonly requiresAny?: never;
    }
    | {
      readonly requires?: never;
      readonly requiresAll?: never;
      readonly requiresAny?: never;
    }
  );

export const useHasPermissions = (props: PermissionProps): boolean => {
  const { membership } = useOrganization();
  if (!membership) return false;

  const permissions = new Set(membership.permissions ?? []);
  const role = membership.role;

  if (props.requiresSuperAdmin === true && role !== "org:super_admin") {
    return false;
  }

  if ("requires" in props && props.requires !== undefined) {
    return permissions.has(props.requires);
  } else if ("requiresAll" in props && props.requiresAll !== undefined) {
    return props.requiresAll.every((permission) => permissions.has(permission));
  } else if ("requiresAny" in props && props.requiresAny !== undefined) {
    return props.requiresAny.some((permission) => permissions.has(permission));
  }

  if (props.requiresSuperAdmin === true) {
    return true;
  }

  return false;
};
